import type { NoteBracketSide } from "@/lib/sketch-stroke/sketch-shapes";

/** Gap between a mark and its note's bracket. */
const NOTE_BRACKET_GAP_PX = 12;
/** Gap between a mark and its note's handwriting. */
const NOTE_TEXT_GAP_PX = 22;
/** Width of the drawn bracket (`noteBracketPoints`). */
export const NOTE_BRACKET_WIDTH_PX = 9;
/** Gap between an arrow's tail and its note. */
const ARROW_TAIL_GAP_PX = 12;
const VIEWPORT_INSET_PX = 8;
const MIN_BRACKET_HEIGHT_PX = 18;

interface Point {
  x: number;
  y: number;
}

export interface NoteRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface NoteSize {
  width: number;
  height: number;
}

interface HandwrittenCalloutPlacement {
  /** Display-local top-left of the handwriting. */
  left: number;
  top: number;
  /** The bracket beside the mark; absent for arrows and notes above or below. */
  bracket: {
    side: NoteBracketSide;
    left: number;
    top: number;
    height: number;
  } | null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/** Places a handwritten note beside its mark, in display-local pixels.
 * Shapes and paths try right, left, below, then above, with the bracket
 * 12 px and the text 22 px off the mark and their tops aligned; with no side
 * free the roomiest wins. An arrow's note sits beyond its tail, away from the
 * tip. The note always stays 8 px inside the viewport. */
export function handwrittenCalloutPlacement(
  target: { mark: NoteRect; arrow?: { tail: Point; tip: Point } },
  note: NoteSize,
  viewport: NoteSize,
): HandwrittenCalloutPlacement {
  const minLeft = VIEWPORT_INSET_PX;
  const maxLeft = viewport.width - VIEWPORT_INSET_PX - note.width;
  const minTop = VIEWPORT_INSET_PX;
  const maxTop = viewport.height - VIEWPORT_INSET_PX - note.height;
  const place = (left: number, top: number) => ({
    left: clamp(left, minLeft, maxLeft),
    top: clamp(top, minTop, maxTop),
  });

  if (target.arrow) {
    const { tail, tip } = target.arrow;
    const dx = tip.x - tail.x;
    const dy = tip.y - tail.y;
    const origin =
      Math.abs(dx) >= Math.abs(dy)
        ? place(
            dx > 0
              ? tail.x - ARROW_TAIL_GAP_PX - note.width
              : tail.x + ARROW_TAIL_GAP_PX,
            tail.y - note.height / 2,
          )
        : place(
            tail.x - note.width / 2,
            dy > 0
              ? tail.y - ARROW_TAIL_GAP_PX - note.height
              : tail.y + ARROW_TAIL_GAP_PX,
          );
    return { ...origin, bracket: null };
  }

  const { mark } = target;
  const room = {
    right: viewport.width - VIEWPORT_INSET_PX - mark.right - NOTE_TEXT_GAP_PX,
    left: mark.left - NOTE_TEXT_GAP_PX - VIEWPORT_INSET_PX,
    below: viewport.height - VIEWPORT_INSET_PX - mark.bottom - NOTE_TEXT_GAP_PX,
    above: mark.top - NOTE_TEXT_GAP_PX - VIEWPORT_INSET_PX,
  };
  const fits = {
    right: room.right >= note.width,
    left: room.left >= note.width,
    below: room.below >= note.height,
    above: room.above >= note.height,
  };
  const order = ["right", "left", "below", "above"] as const;
  const side =
    order.find((candidate) => fits[candidate]) ??
    order.reduce((best, candidate) =>
      room[candidate] > room[best] ? candidate : best,
    );

  if (side === "below" || side === "above") {
    return {
      ...place(
        mark.left,
        side === "below"
          ? mark.bottom + NOTE_TEXT_GAP_PX
          : mark.top - NOTE_TEXT_GAP_PX - note.height,
      ),
      bracket: null,
    };
  }

  const bracketTop = clamp(
    mark.top,
    VIEWPORT_INSET_PX,
    viewport.height - VIEWPORT_INSET_PX - MIN_BRACKET_HEIGHT_PX,
  );
  const bracketBottom = Math.min(
    mark.bottom,
    viewport.height - VIEWPORT_INSET_PX,
  );
  const bracket = {
    side,
    left:
      side === "right"
        ? mark.right + NOTE_BRACKET_GAP_PX
        : mark.left - NOTE_BRACKET_GAP_PX - NOTE_BRACKET_WIDTH_PX,
    top: bracketTop,
    height: Math.max(MIN_BRACKET_HEIGHT_PX, bracketBottom - bracketTop),
  } as const;
  return {
    ...place(
      side === "right"
        ? mark.right + NOTE_TEXT_GAP_PX
        : mark.left - NOTE_TEXT_GAP_PX - note.width,
      mark.top,
    ),
    bracket,
  };
}
